import React, { useState, useEffect, useCallback } from 'react';
import { Send, ShieldCheck, Clock, CheckCircle2, Loader2, ChevronDown, ChevronUp, FileText, User, X, Sparkles, Hourglass, RotateCcw, CalendarCheck, CreditCard } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { isFirmLike } from '../../lib/orgTypes';

import { useOrg } from '../../hooks/useOrg';
import { useLocations } from '../../hooks/useLocations';
import { BillTo, FundingReasons, PacketChip } from './CollectabilityBadge';
import { packetView } from '../../lib/referrals/packet';
import ReferralBookingModal from './ReferralBookingModal';
import OfficeHoursEditor, { summarizeHours } from '../locations/OfficeHoursEditor';

// The referral inbox. Law firms see what they've sent (and what their
// clients requested from the portal) and where each one stands; medical
// practices see what's arrived with the full case file and work it:
// accept within the hour → schedule the first visit → care completed →
// records delivered. Expired and declined referrals hand the case back to
// the firm to send elsewhere.

const STATUS_META = {
  received:  ['bg-accent-glow text-accent-light', 'Awaiting acceptance'],
  accepted:  ['bg-accent-glow text-accent-light', 'Accepted — patient choosing a time'],
  scheduled: ['bg-warning-soft text-warning', 'First visit scheduled'],
  completed: ['bg-warning-soft text-warning', 'Care completed'],
  delivered: ['bg-success-soft text-success', 'Records delivered'],
  declined:  ['bg-danger-soft text-danger', 'Declined'],
  expired:   ['bg-danger-soft text-danger', 'Expired — no answer'],
  draft:     ['bg-tertiary text-muted-c', 'Draft'],
};

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const fmtMoneyish = (n) => (n == null || n === '' ? '—' : `$${Number(n).toLocaleString()}`);

// Minutes left in the acceptance window; ticks once a minute while mounted.
function useCountdown(iso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30 * 1000); return () => clearInterval(t); }, []);
  if (!iso) return null;
  return Math.max(0, Math.round((new Date(iso).getTime() - now) / 60000));
}

// The hours the patient (or the firm) will book against, on the referral
// itself. Published hours show as one line with an Edit toggle; an office
// with nothing published opens the editor so the clinic sets its schedule
// before — or right after — accepting, instead of the 8–6 default.
function ReferralOfficeHours({ orgId, locationId, officeName, patientName, status }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!orgId || !locationId) return;
    api.getLocationHours(orgId, locationId).then((h) => { setRows(h || []); setEditing((h || []).length === 0); }).catch(() => setRows([]));
  }, [orgId, locationId]);
  if (!locationId || rows === null) return null;
  const summary = summarizeHours(rows);
  return (
    <div className="rounded-[12px] border border-theme p-4 mb-3">
      <div className="flex items-center gap-3 flex-wrap">
        <Clock size={15} className="text-accent-light shrink-0" />
        <div className="flex-1 min-w-[220px]">
          <div className="text-[13px] font-semibold text-primary-c">
            {summary ? `${officeName || 'Office'} hours` : `Set ${officeName ? `${officeName}'s` : 'your'} hours`}
          </div>
          <div className="text-[11.5px] text-muted-c">
            {summary
              ? `${summary}. ${patientName || 'The patient'} ${status === 'accepted' ? 'is choosing' : 'will choose'} a time inside these hours.`
              : `No schedule published yet, so booking would offer 8 AM–6 PM every day. Set when ${officeName || 'the office'} is open and ${patientName || 'the patient'} books inside it.`}
          </div>
        </div>
        {summary && (
          <button onClick={() => setEditing((v) => !v)} className="text-[12px] font-semibold text-accent-light hover:text-accent">{editing ? 'Done' : 'Edit hours'}</button>
        )}
      </div>
      {editing && (
        <div className="mt-3">
          <OfficeHoursEditor orgId={orgId} locationId={locationId} officeName={officeName} compact initialRows={rows} onSaved={(blocks) => { setRows(blocks); }} />
        </div>
      )}
    </div>
  );
}

// Offer up to three first-visit times (076). Defaults to the next three
// business days at 10:00 so the common case is one click; each row is a
// date + time, one optional provider for all of them. The server turns
// these into the patient's text ("Reply 1, 2 or 3"), the quick page's
// one-tap choices and the firm's confirm buttons.
const OFFER_TIMES = [];
for (let h = 7; h <= 18; h++) for (const m of [0, 30]) if (!(h === 18 && m === 30)) OFFER_TIMES.push(`${String(h).padStart(2, '0')}:${m === 0 ? '00' : '30'}`);
const toInputDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function nextBusinessDays(n) {
  const out = []; const d = new Date();
  while (out.length < n) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) out.push(toInputDate(d)); }
  return out;
}
const fmtTimeOpt = (t) => { const [h, m] = t.split(':').map(Number); return new Date(2000, 0, 1, h, m).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); };

function OfferTimesCard({ orgId, locationId, patientName, mode, busy, onSend, onAcceptWithout, onCancel }) {
  const [rows, setRows] = useState(() => nextBusinessDays(3).map((date) => ({ date, time: '10:00' })));
  const [providers, setProviders] = useState([]);
  const [providerId, setProviderId] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    api.getOrgProviders(orgId, locationId || undefined).then((p) => setProviders(p || [])).catch(() => setProviders([]));
  }, [orgId, locationId]);
  const minDate = toInputDate(new Date());
  const update = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const remove = (i) => setRows((rs) => rs.filter((_, j) => j !== i));
  const add = () => setRows((rs) => [...rs, { date: nextBusinessDays(rs.length + 1).slice(-1)[0], time: '10:00' }]);
  const submit = () => {
    const times = [];
    for (const r of rows) {
      if (!r.date || !r.time) continue;
      const [y, mo, d] = r.date.split('-').map(Number); const [h, mi] = r.time.split(':').map(Number);
      const dt = new Date(y, mo - 1, d, h, mi, 0, 0);
      if (dt.getTime() < Date.now()) { setErr(`${dt.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })} has already passed.`); return; }
      times.push({ starts_at: dt.toISOString(), provider_id: providerId || null });
    }
    if (!times.length) { setErr('Add at least one time.'); return; }
    setErr('');
    onSend(times, Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York');
  };
  const first = (patientName || 'the patient').split(' ')[0];
  return (
    <div className="rounded-[12px] border border-accent/40 bg-accent-glow/40 p-4 mb-3">
      <div className="flex items-start gap-3">
        <CalendarCheck size={15} className="text-accent-light shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-primary-c">{mode === 'accept' ? `Accept and offer ${first} first-visit times` : `Offer ${first} new first-visit times`}</div>
          <div className="text-[11.5px] text-muted-c">
            Up to three. {first} gets them by text and email and confirms one by replying 1, 2 or 3 — or taps one on the link. The firm can confirm for them too.
          </div>
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {rows.map((row, i) => (
          <div key={i} className="flex items-center gap-2 flex-wrap">
            <span className="w-[18px] h-[18px] rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
            <input type="date" min={minDate} value={row.date} onChange={(e) => update(i, { date: e.target.value })} className="px-3 py-[7px] rounded-[9px] bg-tertiary border border-theme text-[12.5px] text-primary-c focus:outline-none focus:border-strong" />
            <select value={row.time} onChange={(e) => update(i, { time: e.target.value })} className="px-3 py-[7px] rounded-[9px] bg-tertiary border border-theme text-[12.5px] text-primary-c focus:outline-none focus:border-strong">
              {OFFER_TIMES.map((t) => <option key={t} value={t}>{fmtTimeOpt(t)}</option>)}
            </select>
            {rows.length > 1 && (
              <button type="button" onClick={() => remove(i)} className="w-7 h-7 rounded-[8px] text-muted-c hover:text-danger hover:bg-danger-soft flex items-center justify-center" title="Remove this time"><X size={12} /></button>
            )}
          </div>
        ))}
        <div className="flex items-center gap-3 flex-wrap pt-1">
          {rows.length < 3 && <button type="button" onClick={add} className="text-[12px] font-semibold text-accent-light hover:text-accent">+ Add a time</button>}
          {providers.length > 1 && (
            <select value={providerId} onChange={(e) => setProviderId(e.target.value)} className="px-3 py-[7px] rounded-[9px] bg-tertiary border border-theme text-[12.5px] text-primary-c focus:outline-none focus:border-strong">
              <option value="">Any provider</option>
              {providers.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
            </select>
          )}
        </div>
      </div>
      {err && <div className="text-[12px] text-danger mt-2">{err}</div>}
      <div className="flex items-center gap-2 flex-wrap mt-3">
        <button type="button" onClick={submit} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-[8px] rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} {mode === 'accept' ? 'Accept & send these times' : 'Send these times'}
        </button>
        {mode === 'accept' && (
          <button type="button" onClick={onAcceptWithout} disabled={busy} className="inline-flex items-center gap-1.5 px-3.5 py-[8px] rounded-[10px] text-[12px] font-semibold text-accent-light bg-accent-glow border border-theme disabled:opacity-60">
            Accept without times
          </button>
        )}
        <button type="button" onClick={onCancel} disabled={busy} className="text-[12px] font-semibold text-muted-c hover:text-primary-c px-2">Cancel</button>
      </div>
    </div>
  );
}

// The times on offer, as chips. The firm gets a Confirm button on each.
function OfferedTimes({ offers, onConfirm, busy }) {
  return (
    <div className="flex flex-wrap gap-2 mt-2.5">
      {offers.map((o, i) => {
        const past = new Date(o.starts_at) < new Date();
        return (
          <div key={o.starts_at} className={`inline-flex items-center gap-2 rounded-[9px] border border-theme px-2.5 py-1.5 text-[12px] ${past ? 'opacity-50 line-through' : 'bg-tertiary'}`}>
            <span className="w-[16px] h-[16px] rounded-full bg-accent text-white text-[9.5px] font-bold flex items-center justify-center">{i + 1}</span>
            <span className="text-primary-c font-medium">{new Date(o.starts_at).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: o.timezone || undefined })}</span>
            {o.provider_name && <span className="text-muted-c">· {o.provider_name}</span>}
            {onConfirm && !past && (
              <button type="button" onClick={() => onConfirm(i + 1)} disabled={busy} className="ml-1 text-[11px] font-semibold text-accent-light hover:text-accent disabled:opacity-50">Confirm</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AcceptWindow({ r, isFirm }) {
  const mins = useCountdown(r.expires_at);
  if (r.status !== 'received' || mins == null) return null;
  const cls = mins <= 10 ? 'text-danger' : mins <= 25 ? 'text-warning' : 'text-muted-c';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${cls}`}>
      <Hourglass size={11} /> {mins === 0 ? 'Window closing…' : `${mins} min left${isFirm ? ' for the clinic to accept' : ' to accept'}`}
    </span>
  );
}

export default function ReferralsPage() {
  const { currentOrg } = useOrg();
  const navigate = useNavigate();
  const isFirm = isFirmLike(currentOrg?.type);
  // A clinic working in one office sees that office's referrals, like its calendar.
  const { apiLocationFilter, locations, currentLocation } = useLocations();
  const locationFilter = isFirm ? undefined : apiLocationFilter;
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  // Trial ended without a card: referrals still land here, but accepting one
  // needs a payment method first. The banner says so before they try.
  const { subscription } = useSubscription();
  const paused = !isFirm && subscription?.status === 'paused';
  const [openingBilling, setOpeningBilling] = useState(false);
  const addCard = async () => {
    setOpeningBilling(true);
    try { const { url } = await api.createPortalSession(currentOrg.id, { flow: 'add_card' }); window.location.href = url; }
    catch (err) { setError(err.message); setOpeningBilling(false); }
  };
  const [notEnabled, setNotEnabled] = useState(false);
  const [openId, setOpenId] = useState(null);
  // ?open=<id> — the link in the clinic's text/email lands on that referral, expanded.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkId = searchParams.get('open');
  const [busyId, setBusyId] = useState(null);
  const [reminded, setReminded] = useState({}); // referral id → true after "Resend invite"
  const [booking, setBooking] = useState(null); // referral the firm is booking on the patient's behalf
  const [offeringId, setOfferingId] = useState(null); // referral whose offer-times card is open (clinic)

  const load = useCallback(async () => {
    if (!currentOrg?.id) return;
    try {
      const data = await api.listReferrals(currentOrg.id, isFirm ? 'sent' : 'received', locationFilter ? { location_id: locationFilter } : {});
      setRows(data);
      setError('');
    } catch (err) {
      if (err.status === 404) setNotEnabled(true);
      else setError(err.message);
      setRows([]);
    }
  }, [currentOrg?.id, isFirm, locationFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!deepLinkId || !rows) return;
    if (rows.some((r) => r.id === deepLinkId)) {
      setOpenId(deepLinkId);
      setTimeout(() => document.getElementById(`referral-${deepLinkId}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 50);
    }
    setSearchParams((p) => { p.delete('open'); return p; }, { replace: true });
  }, [deepLinkId, rows, setSearchParams]);
  // The acceptance window moves without user action — refresh the list every minute.
  useEffect(() => { const t = setInterval(load, 60 * 1000); return () => clearInterval(t); }, [load]);

  const advance = async (r, to, note, extra) => {
    setBusyId(r.id);
    setError('');
    try {
      await api.updateReferralStatus(currentOrg.id, r.id, to, note || '', extra);
      await load();
    } catch (err) {
      setError(err.message);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  // Clinic: offer (or replace) first-visit times on an already-accepted referral.
  const sendOffers = async (r, times, timezone) => {
    setBusyId(r.id);
    setError('');
    try { await api.proposeReferralTimes(currentOrg.id, r.id, { times, timezone }); setOfferingId(null); await load(); }
    catch (err) { setError(err.message); }
    finally { setBusyId(null); }
  };
  // Firm: confirm one of the offered times for the client (they're on the phone).
  const confirmOffer = async (r, option) => {
    setBusyId(r.id);
    setError('');
    try { await api.bookReferralVisit(currentOrg.id, r.id, { option, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }); await load(); }
    catch (err) { setError(err.message); await load(); }
    finally { setBusyId(null); }
  };

  const resend = (r) => navigate(`/app/referrals/concierge?client=${r.client_id}&return=${encodeURIComponent('/app/referrals')}`);

  const remind = async (r) => {
    setBusyId(r.id);
    setError('');
    try { await api.remindReferralBooking(currentOrg.id, r.id); setReminded((m) => ({ ...m, [r.id]: true })); }
    catch (err) { setError(err.message); }
    finally { setBusyId(null); }
  };

  if (notEnabled) {
    return (
      <div className="max-w-[760px] mx-auto px-6 py-16 text-center">
        <ShieldCheck size={26} className="text-muted-c mx-auto mb-3" />
        <h1 className="text-[17px] font-bold text-primary-c mb-1">Referrals aren't enabled here yet</h1>
        <p className="text-[13px] text-muted-c">This feature is rolling out — check back soon.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1060px] mx-auto px-4 md:px-6 py-7">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Send size={15} className="text-accent-light" />
            <span className="text-[11px] uppercase tracking-wide text-accent-light font-semibold">Referrals</span>
          </div>
          <h1 className="text-[24px] font-bold text-primary-c">
            {isFirm ? 'Referrals you’ve sent' : 'Referral inbox'}
          </h1>
          <p className="text-[13px] text-muted-c mt-1 max-w-[600px]">
            {isFirm
              ? 'Every referral your firm — or your clients from their portal — has sent. Clinics have one hour to accept; your client then books the first visit from the clinic\'s open slots.'
              : 'Each referral arrives with the complete case file. Accept within the hour; the patient then books the first visit from your open slots, and the firm is notified at every step.'}
            {!isFirm && locationFilter && currentLocation && ` Showing ${currentLocation.name}${locations.length > 1 ? ' — switch offices in the header to see the rest' : ''}.`}
          </p>
        </div>
        {isFirm && (
          <button
            onClick={() => navigate('/app/cases')}
            className="px-4 h-[38px] rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow flex items-center gap-1.5"
          >
            <Sparkles size={13} /> Find a provider
          </button>
        )}
      </div>

      {paused && (
        <div className="rounded-[12px] border border-warning/40 bg-warning-soft px-4 py-3 mb-5 flex items-center gap-3 flex-wrap">
          <CreditCard size={15} className="text-warning flex-shrink-0" />
          <div className="flex-1 min-w-[220px] text-[12.5px] text-primary-c">
            <span className="font-semibold">Your free trial has ended.</span> Referrals still arrive here, but you need to add a payment method before you can accept one.
          </div>
          <button onClick={addCard} disabled={openingBilling} className="px-3.5 h-[34px] rounded-[9px] text-[12px] font-semibold text-white gradient-accent shadow-glow flex items-center gap-1.5 disabled:opacity-60">
            {openingBilling ? <Loader2 size={13} className="animate-spin" /> : <CreditCard size={13} />} Add a card & reactivate
          </button>
        </div>
      )}

      {error && (
        <div className="text-[12px] text-danger bg-danger-soft rounded-[10px] px-3.5 py-2.5 mb-5">{error}</div>
      )}

      {rows === null ? (
        <p className="text-[13px] text-muted-c flex items-center gap-2 py-10 justify-center">
          <Loader2 size={15} className="animate-spin" /> Loading referrals…
        </p>
      ) : rows.length === 0 ? (
        <div className="bg-elevated rounded-[14px] border border-theme px-6 py-14 text-center">
          <Send size={22} className="text-muted-c mx-auto mb-3" />
          <p className="text-[14px] font-semibold text-primary-c">
            {isFirm ? 'No referrals sent yet' : 'No referrals yet'}
          </p>
          <p className="text-[12.5px] text-muted-c mt-1 max-w-[420px] mx-auto">
            {isFirm
              ? 'Open a case and hit Find a Provider — the packet assembles from the file and lands here when sent.'
              : 'Referrals from partnered firms appear here with the full case file attached.'}
          </p>
        </div>
      ) : (
        <div className="bg-elevated rounded-[14px] border border-theme overflow-hidden">
          {rows.map((r, i) => {
            const [cls, label] = STATUS_META[r.status] || STATUS_META.received;
            const open = openId === r.id;
            const ins = r.insurance || {};
            const busy = busyId === r.id;
            const canWithdraw = isFirm && ['received', 'accepted'].includes(r.status);
            const canDecline = !isFirm && ['received', 'accepted'].includes(r.status);
            const needsResend = isFirm && ['expired', 'declined'].includes(r.status);
            return (
              <div key={r.id} id={`referral-${r.id}`} className={i > 0 ? 'border-t border-theme' : ''}>
                <button
                  onClick={() => setOpenId(open ? null : r.id)}
                  className="w-full text-left px-5 py-4 hover:bg-hover-surface transition-all flex items-center gap-4"
                >
                  <div className={`w-[38px] h-[38px] rounded-[10px] flex items-center justify-center flex-shrink-0 ${cls}`}>
                    <ShieldCheck size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-[13px] font-semibold text-primary-c truncate">
                        {r.client_snapshot?.name || 'Patient'}
                      </span>
                      {r.via === 'patient' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[6px] text-[10px] font-semibold bg-accent-glow text-accent-light">
                          <User size={9} /> Patient-requested
                        </span>
                      )}
                      {r.service && <span className="text-[11.5px] text-muted-c truncate">{r.service}</span>}
                    </div>
                    <div className="text-[11.5px] text-muted-c flex items-center gap-2 flex-wrap">
                      <span className="min-w-0 break-words">
                        {isFirm ? `To ${r.provider?.name || '—'}` : `From ${r.firm?.name || '—'}`}
                        {r.location?.name && (isFirm || locations.length > 1) ? ` · ${r.location.name}` : ''}
                        {' · '}{fmtDT(r.created_at)}
                        {r.scheduled_time ? ` · first visit ${fmtDT(r.scheduled_time)}` : ''}
                      </span>
                      <AcceptWindow r={r} isFirm={isFirm} />
                    </div>
                  </div>
                  <div className="hidden md:flex items-center gap-2 flex-shrink-0">
                    {r.billing?.chip && <span className="text-[11px] font-semibold text-secondary-c bg-tertiary rounded-md px-2 py-0.5 whitespace-nowrap">{r.billing.chip}</span>}
                    <PacketChip referral={r} />
                    <span className={`px-2.5 py-1 rounded-[8px] text-[11px] font-semibold ${cls}`}>{label}</span>
                  </div>
                  {open ? <ChevronUp size={14} className="text-muted-c shrink-0" /> : <ChevronDown size={14} className="text-muted-c shrink-0" />}
                </button>

                {open && (
                  <div className="px-5 pb-5 pt-1">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px] bg-tertiary rounded-[12px] p-4 mb-3">
                      <div><div className="text-muted-c text-[10.5px] uppercase font-semibold">DOB</div><div className="text-primary-c font-medium">{r.client_snapshot?.dob || '—'}</div></div>
                      <div><div className="text-muted-c text-[10.5px] uppercase font-semibold">Date of loss</div><div className="text-primary-c font-medium">{r.client_snapshot?.date_of_loss || '—'}</div></div>
                      <div><div className="text-muted-c text-[10.5px] uppercase font-semibold">Carrier · claim</div><div className="text-primary-c font-medium truncate">{ins.carrier || '—'}{ins.claim_number ? ` · ${ins.claim_number}` : ''}</div></div>
                      <div><div className="text-muted-c text-[10.5px] uppercase font-semibold">BI limits</div><div className="text-primary-c font-medium">{ins.bi_per_person ? `${fmtMoneyish(ins.bi_per_person)} / ${fmtMoneyish(ins.bi_per_accident)}` : '—'}</div></div>
                    </div>
                    {r.billing?.items && (
                      <div className="mb-3">
                        <BillTo referral={r} compact orgId={currentOrg.id} />
                        <FundingReasons referral={r} className="mt-2.5 px-1" />
                      </div>
                    )}
                    {(() => { const pv = packetView(r); return (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {pv.items.map((it) => (
                          <span key={it.key} className={`inline-flex items-center gap-1.5 text-[10.5px] rounded-md px-2 py-1 max-w-[260px] truncate ${it.done ? 'bg-success-soft text-success' : it.required === false ? 'bg-tertiary text-muted-c' : 'bg-warning-soft text-warning'}`} title={it.file || it.hint || ''}>
                            {it.done ? <CheckCircle2 size={10} className="shrink-0" /> : <FileText size={10} className="shrink-0" />} {it.file || it.label}{it.required === false && !it.done ? ' (optional)' : ''}
                          </span>
                        ))}
                      </div>
                    ); })()}

                    {/* Clinic: the office's hours are what the patient books against — set them right here */}
                    {!isFirm && ['received', 'accepted'].includes(r.status) && (
                      <ReferralOfficeHours
                        orgId={currentOrg.id}
                        locationId={r.location_id || locations.find((l) => l.is_primary)?.id || locations[0]?.id}
                        officeName={r.location?.name || locations.find((l) => l.is_primary)?.name || locations[0]?.name}
                        patientName={r.client_snapshot?.name}
                        status={r.status}
                      />
                    )}

                    {/* Clinic: accept-and-offer card (received) or new-times card (accepted) */}
                    {!isFirm && offeringId === r.id && ['received', 'accepted'].includes(r.status) && (
                      <OfferTimesCard
                        orgId={currentOrg.id}
                        locationId={r.location_id || locations.find((l) => l.is_primary)?.id || locations[0]?.id}
                        patientName={r.client_snapshot?.name}
                        mode={r.status === 'received' ? 'accept' : 'reoffer'}
                        busy={busy}
                        onSend={(times, timezone) => (r.status === 'received'
                          ? advance(r, 'accepted', '', { proposed_times: times, timezone }).then(() => setOfferingId(null))
                          : sendOffers(r, times, timezone))}
                        onAcceptWithout={() => advance(r, 'accepted').then(() => setOfferingId(null))}
                        onCancel={() => setOfferingId(null)}
                      />
                    )}

                    {/* Accepted: offered times waiting on the patient, or the open-slot link */}
                    {r.status === 'accepted' && offeringId !== r.id && (() => {
                      const offers = Array.isArray(r.proposed_times) ? r.proposed_times : [];
                      const declined = !!r.proposed_declined_at;
                      const first = (r.client_snapshot?.name || 'the patient').split(' ')[0];
                      const hasOffers = offers.length > 0 && !declined;
                      return (
                        <div className={`rounded-[12px] border p-4 mb-3 ${declined ? 'border-warning/40 bg-warning-soft' : 'border-theme'}`}>
                          <div className="flex items-center gap-3 flex-wrap">
                            <Hourglass size={15} className={`${declined ? 'text-warning' : 'text-accent-light'} shrink-0`} />
                            <div className="flex-1 min-w-[220px]">
                              <div className="text-[13px] font-semibold text-primary-c">
                                {declined
                                  ? `${first} can't make the times offered`
                                  : hasOffers
                                    ? `${offers.length} time${offers.length === 1 ? '' : 's'} offered — waiting for ${first} to confirm`
                                    : `Waiting for ${r.client_snapshot?.name || 'the patient'} to choose a time`}
                              </div>
                              <div className="text-[11.5px] text-muted-c">
                                {declined
                                  ? `They said so on ${fmtDT(r.proposed_declined_at)}. ${isFirm ? (r.provider?.name || 'The clinic') + ' was asked to offer new times; you can also book from their open slots.' : 'Offer new times, or they can still pick from your open slots.'}`
                                  : hasOffers
                                    ? `Sent by text and email${r.booking_invited_at ? ` on ${fmtDT(r.booking_invited_at)}` : ''}. ${first} replies 1, 2 or 3 to confirm, or taps one on the link.${isFirm ? ' On the phone with them? Confirm it here.' : ''}`
                                    : `They were sent a link to book from ${isFirm ? (r.provider?.name || 'the clinic') + "'s" : 'your'} open slots${r.booking_invited_at ? ` on ${fmtDT(r.booking_invited_at)}` : ''}. It books on the spot — no confirmation step.`}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              {!isFirm && (
                                <button onClick={() => setOfferingId(r.id)} disabled={busy} className="inline-flex items-center gap-1.5 px-3.5 py-[8px] rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-60">
                                  <CalendarCheck size={13} /> {hasOffers ? 'Offer different times' : 'Offer times'}
                                </button>
                              )}
                              {isFirm && (
                                <button onClick={() => setBooking(r)} className="inline-flex items-center gap-1.5 px-3.5 py-[8px] rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow">
                                  <CalendarCheck size={13} /> {hasOffers ? 'Pick another time' : `Book for ${first}`}
                                </button>
                              )}
                              <button onClick={() => remind(r)} disabled={busy || reminded[r.id]} className="inline-flex items-center gap-1.5 px-3.5 py-[8px] rounded-[10px] text-[12px] font-semibold text-accent-light bg-accent-glow border border-theme disabled:opacity-60">
                                {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} {reminded[r.id] ? 'Sent again' : hasOffers ? 'Resend the times' : 'Resend invite'}
                              </button>
                            </div>
                          </div>
                          {hasOffers && <OfferedTimes offers={offers} onConfirm={isFirm ? (n) => confirmOffer(r, n) : null} busy={busy} />}
                        </div>
                      );
                    })()}

                    <div className="flex items-center gap-2 flex-wrap">
                      {!isFirm && r.status === 'received' && offeringId !== r.id && (
                        <button
                          onClick={() => setOfferingId(r.id)}
                          disabled={busy}
                          className="inline-flex items-center gap-1.5 px-4 py-[8px] rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50"
                          title="Offer the patient up to three first-visit times, or let them pick from your open slots"
                        >
                          {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Accept referral
                        </button>
                      )}
                      {!isFirm && r.status === 'scheduled' && (
                        <button onClick={() => advance(r, 'completed')} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-[8px] rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50">
                          {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Mark care completed
                        </button>
                      )}
                      {!isFirm && r.status === 'completed' && (
                        <button onClick={() => advance(r, 'delivered')} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-[8px] rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50">
                          {busy ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />} Deliver records
                        </button>
                      )}
                      {needsResend && (
                        <button onClick={() => resend(r)} className="inline-flex items-center gap-1.5 px-4 py-[8px] rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow">
                          <RotateCcw size={13} /> Send to another provider
                        </button>
                      )}
                      {(canDecline || canWithdraw) && (
                        <button
                          onClick={() => advance(r, 'declined')}
                          disabled={busy}
                          className="inline-flex items-center gap-1.5 px-3.5 py-[8px] rounded-[10px] text-[12px] font-semibold text-danger bg-danger-soft border border-theme disabled:opacity-50"
                        >
                          <X size={13} /> {isFirm ? 'Withdraw' : 'Decline'}
                        </button>
                      )}
                      {(r.timeline || []).length > 0 && (
                        <span className="text-[11px] text-muted-c flex items-center gap-1 ml-auto">
                          <Clock size={11} /> {(r.timeline || []).slice(-1)[0]?.note || label}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {booking && (
        <ReferralBookingModal
          orgId={currentOrg.id}
          referral={booking}
          onClose={() => setBooking(null)}
          onBooked={async () => { setBooking(null); await load(); }}
        />
      )}
    </div>
  );
}
